import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useLocation } from 'react-router-dom';
import {baseUrl , reportTypeList} from "../../comman/config";
import { itemSelectOption, matchItemOption } from "../ItemName";
import { readReportLink } from "./reportLink";

// Opt-in props, all defaulting to the previous behaviour. This filter is shared by eight
// screens (Raw Report, Master Report, Summary, Production, Excess, Rotli/Bhakhri, Recipe
// Feedback, Old Master Report), so Raw Report and Master Report opt in without disturbing
// the other six:
//   multiEvent:         several events at once, collapsed to earliest start -> latest end.
//   syncToDate:         changing From Date moves To Date with it (still editable afterwards).
//   handleSubmitFilters: explicit refetch. Without it, Submit only re-runs the parent's
//                       useEffect when a value actually changed, so pressing Submit with an
//                       unchanged form did nothing at all.
//   handleClearFilters: Clear also resets the screen's inline per-column filters.
const DateSessionReportFilter = ({ handleFilterChange, handleExport, handelReportChange, defaultFromDate, defaultToDate,
    multiEvent = false, syncToDate = false, handleSubmitFilters, showReportType = false,
    handleClearFilters }) => {
    const today = new Date().toISOString().split('T')[0];
    const location = useLocation();
    // Opened from another screen with its filter already decided -- see Filter/reportLink.js.
    // Null on every ordinary entry, and then nothing below this changes behaviour. Memoised so it
    // keeps one identity: the submit-once effect below has it in its dependencies.
    const reportLink = useMemo(() => readReportLink(location.search), [location.search]);
    const [fromDate, setFromDate] = useState(reportLink?.fromDate || defaultFromDate || today);
    const [toDate, setToDate] = useState(reportLink?.toDate || defaultToDate || today);
    const [session, setSession] = useState([]);
    const [reportType, setReportType] = useState("");
    const [item, setItem] = useState([]);
    const [method, setMethod] = useState([]);
    const [sessionList, setSessionList] = useState([]);
    const [itemList, setItemList] = useState([]);
    const [methodList, setMethodList] = useState([]);
    const [filter, setFilter] = useState(false);
    const [eventList, setEventList] = useState([]);
    // Array when multiEvent, single object otherwise -- react-select needs the shape to match isMulti.
    const ALL_EVENT = { value: 'all', label: 'All' };
    const [event, setEvent] = useState(multiEvent ? [ALL_EVENT] : {});
    // const [summary, setSummary] = useState(false);
    const routeName = location.pathname.split('/').pop();
    // Master Report ("/reports/new_master") restricts the Method filter to these method_master
    // ids: 1=Kgs, 4=Readymade-Kgs, 5=Readymade-Ltrs, 6=Readymade-Nos.
    const isNewMaster = routeName === "new_master";
    // Raw Report ("/reports/master") opens on Kgs, Nos and Readymade-Nos -- the three the client
    // reads it for -- where it used to open on every method.
    //
    // Matched on the FULL path, not routeName. The Old Master Report lives at
    // "/old_reports/master", so the two share the last segment and routeName cannot tell them
    // apart; keying on it would have moved the Old Master Report's default along with this one.
    // The Report List note further down is the same trap, hit once already. The Old Master Report
    // keeps opening on every method, which is what the two shared until now.
    const isRawReport = location.pathname === "/reports/master";
    const isOldMasterReport = location.pathname === "/old_reports/master";
    // method_master ids: 1 = Kgs, 3 = Nos, 6 = Readymade-Nos.
    const RAW_REPORT_METHOD_IDS = [1, 3, 6];

    // Which methods a report starts with, used by both the first load and Clear.
    const defaultMethodIds = (list) => list.filter((item) => {
        if (routeName === "rotlibhakhri") return Number(item.id) === 3;
        if (isNewMaster) return NEW_MASTER_METHOD_IDS.includes(Number(item.id));
        if (isRawReport) return RAW_REPORT_METHOD_IDS.includes(Number(item.id));
        if (isOldMasterReport) return true;                 // All
        return [1, 2, 3].includes(Number(item.id));
    });
    const NEW_MASTER_METHOD_IDS = [1, 4, 5, 6];
    const methodOptionList = isNewMaster
        ? methodList.filter((m) => NEW_MASTER_METHOD_IDS.includes(Number(m.id)))
        : methodList;


    useEffect(() => {
        getSessionList();
        getItemList();
        getMethodList();
        getEventList();
    }, []);

    const getSessionList =  async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=session_master&condition=status=1`);
        const sessionList = response.data.data;
        sessionList.unshift({id:"", name:"Session List"});
        setSessionList(sessionList);
        const asOption = (item) => ({ value: item.id, label: item.name });
        // A linked session is the one the report was opened ON -- the Consumption Report's Date
        // column carries the session the clicked row belongs to -- so it replaces the two this
        // filter otherwise opens with. Without a link, nothing here changes.
        setSession(reportLink?.session
            ? sessionList.filter((item) => String(item.id) === String(reportLink.session)).map(asOption)
            : sessionList.filter((item) => item.id == 1 || item.id == 2).map(asOption));
    }

    const getItemList = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=item_master&condition=status=1`);
        const itemList = response.data.data;
        // itemList.unshift({id:"", item_name:'Item List'});
        if(routeName == "rotlibhakhri"){
            setItemList(itemList.filter((item) => item.category == 3))
        }else{
            setItemList(itemList);
        }
        // The linked item can only become a chip once its row is known -- the box shows a label,
        // and the URL carries an id.
        if (reportLink?.item) {
            setItem(itemList.filter((i) => String(i.id) === String(reportLink.item)).map(itemSelectOption));
        }
    }

    const getMethodList = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=method_master&condition=status=1`);
        const methodList = response.data.data;
        // methodList.unshift({id:"", name:'Method List'});
        setMethodList(methodList);
        
        setMethod(defaultMethodIds(methodList).map((item) => ({ value: item.id, label: item.name })));
        // if ((routeName === "excess" || routeName === "production") && methodList.length >= 3) {
        //     setMethod([methodList[0], methodList[2]].map(item => ({
        //         value: item.id,
        //         label: item.name
        //     })));
        // }
    }

    const getEventList = async() => {
        const response = await axios.get(`${baseUrl}/comman/list?table=event_master&condition=status=1`);
        const data = response.data.data;
        const newObject = { id: 'all', event_name: 'All' };
        data.unshift(newObject);
        setEventList(data);
        setEvent(multiEvent ? [ALL_EVENT] : ALL_EVENT)
    }

    const handleDateChange = (e) => {
        const {name, value} = e.target;
        if(name == "from_date"){
            setFromDate(value);
            // Single-day is the common case, so To Date follows From Date. It stays editable --
            // this only fires when From changes, never when the user sets To directly.
            if (syncToDate) setToDate(value);
        }else if(name == "to_date"){
            setToDate(value);
        }

    };

    const handleSelectChange = (data, option) => {
        if(option.name == 'session'){
            setSession(data);
        }else if(option.name == 'method'){
            setMethod(data);
        }else if(option.name == 'reportType'){
            setReportType(data);
        }
        
    };

    // The picked events, as a plain [{value, label}] the parent can forward to the API. "All" (and
    // an empty box) means "do not narrow", so it is sent as []. Only meaningful with multiEvent --
    // the single-select variant already sets the dates to that one event's own span.
    const selectedEvents = () => {
        if (!multiEvent) return [];
        const list = Array.isArray(event) ? event : [];
        return list.filter((o) => o && o.value !== 'all');
    };

    // A linked report runs itself, once, as soon as the lists behind its filter are in. Without
    // this the tab would open on an empty report with the filter filled in and a Submit still to
    // press, which is not what "show data for the menu item selected" means.
    //
    // It waits for the item chip as well as the lists: firing before the chip exists would run the
    // report across every item and then again narrowed, and the reader would see the wrong answer
    // first.
    const linkSubmitted = useRef(false);
    useEffect(() => {
        if (!reportLink || linkSubmitted.current) return;
        if (!sessionList.length || !methodList.length) return;
        if (reportLink.item && !item.length) return;
        linkSubmitted.current = true;
        handleSubmit();
    }, [reportLink, sessionList, methodList, item, session, method]);

    const handleSubmit = () => {
        const events = selectedEvents();
        handleFilterChange(fromDate, toDate, session, item, method, events);
        // Parents that refetch from a useEffect keyed on these values see nothing change when the
        // form is untouched, so Submit appeared dead. handleSubmitFilters refetches explicitly.
        if (handleSubmitFilters) handleSubmitFilters(fromDate, toDate, session, item, method, events);
        setFilter(true);
    };

    const defaultValues = {
        sessions: sessionList
            .filter((item) => item.id === 1 || item.id === 2)
            .map((item) => ({
                value: item.id,
                label: item.name,
            })),
        methods: defaultMethodIds(methodList).map((item) => ({
            value: item.id,
            label: item.name,
        })),
    };

    const handleClear = () => {
        handleFilterChange("", "");
        // Clear takes the inline per-column filters with it. Leaving them set would look like
        // Clear had emptied the report, when a column filter is still hiding the rows.
        handleClearFilters?.();
        setSession(defaultValues.sessions);
        setMethod(defaultValues.methods);
        setItem([]);
        setFromDate(today);
        setToDate(today);
        setEvent(multiEvent ? [ALL_EVENT] : ALL_EVENT);
        setFilter(false);
    }

    // const handleSummary = () => {
    //     setSummary(!summary);
    //     hadelSummary(!summary);
    // }

    const handleReport = (data) => {
        setReportType(data);
        handelReportChange(data);
    }

    const clickExport = () => {
        handleExport();
    }

    const handleChangeMultiSelect = (selected, name) => {
        if(name === 'item'){
            setItem(selected);
        }else if(name === 'method'){
            console.log(selected);
            setMethod(selected);
        }
        
    };

    // NOTE: the parameter used to be named `item`, which shadowed the `item` state -- so picking an
    // event passed the EVENT option through as the item filter. Renamed to `selected`.
    const handelEventChange = (selected) => {
        const data = eventList.filter(i => i.id == selected.value);
        if (!data.length) return;
        setEvent({value: data[0].id, label: data[0].event_name});
        setFromDate(data[0].start_date);
        setToDate(data[0].end_date);
        handleFilterChange(data[0].start_date, data[0].end_date, session, item, method);
    }

    // Multi-select events: the filter carries ONE From/To pair, so several events collapse to the
    // earliest start and the latest end. That only ever widens the window -- anything scheduled in
    // the gaps between the selected events falls inside the span too.
    // Only updates the filter -- the report is regenerated on Submit. Calling handleFilterChange
    // here would set the parent's state, and both screens refetch from a useEffect keyed on it, so
    // every tick would run the report against a half-finished selection.
    const handelEventMultiChange = (selected) => {
        const list = Array.isArray(selected) ? selected : [];
        const real = list.filter((o) => o.value !== 'all');
        // "All" starts selected, so it is still in the list when you tick your first real event.
        // Only treat it as a choice when it was just added -- otherwise it is leftover and gets
        // dropped, or the event you picked would be discarded and the box would snap back to All.
        const allJustClicked = list.some((o) => o.value === 'all') && !(event ?? []).some((o) => o.value === 'all');
        // "All", or clearing the box, drops the event narrowing and leaves the dates alone rather
        // than snapping them back to a default, so the window doesn't jump under the user.
        if (allJustClicked || !real.length) {
            setEvent([ALL_EVENT]);
            return;
        }
        const picked = eventList.filter((e) => e.id !== 'all' && real.some((o) => o.value == e.id));
        const starts = picked.map((e) => e.start_date).filter(Boolean).sort();
        const ends = picked.map((e) => e.end_date).filter(Boolean).sort();
        setEvent(real); // `real`, not `list` -- an "All" chip beside real events reads as a contradiction
        setFromDate(starts[0] ?? fromDate);
        setToDate(ends[ends.length - 1] ?? toDate);
    }

    return (
        <div className="flex flex-column sm:flex-row flex-wrap space-y-4 sm:space-y-0 items-center pb-4">
            <div className='m-5'>
            <Select
                    isMulti={multiEvent}
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    className={multiEvent ? 'basic-multi-select w-64' : 'basic-multi-select w-36'}
                    value={event}
                    name="event_name"
                    onChange={(selectedOptions) => (multiEvent ? handelEventMultiChange(selectedOptions) : handelEventChange(selectedOptions))}
                    options={eventList.map(item => ({
                        value: item.id,
                        label: item.event_name
                    }))}
                    placeholder="Event List"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />  
            </div>
            <div className='m-5'>
                <input
                    type="date"
                    className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    name="from_date"
                    onChange={handleDateChange}
                    value={fromDate}
                />
            </div>
            <div className='m-5'>
                <input
                    type="date"
                    className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    name="to_date"
                    onChange={handleDateChange}
                    value={toDate}
                />
            </div>
            <div className='m-5'>
                <Select
                    isMulti
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    value={session}
                    name="session"
                    onChange={(selectedOption, name) => {
                        handleSelectChange(selectedOption, name);
                    }}
                    options={sessionList.map(item => ({
                        value: item.id,
                        label: item.name
                    }))}
                    placeholder="Session List"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />  
            </div>
            <div className='m-5'>
                <Select
                    isMulti
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    className='basic-multi-select'
                    value={item}
                    name="item"
                    onChange={(selectedOptions) => handleChangeMultiSelect(selectedOptions, 'item')}
                    // The list reads in Gujarati and is searched in either language -- see
                    // components/ItemName.
                    options={itemList.map(itemSelectOption)}
                    placeholder="Item List"
                    filterOption={matchItemOption}
                />  
            </div>
            {(routeName == "rotlibhakhri") ? 
            <div className='m-5'>
                <Select
                    isMulti
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    className='basic-multi-select'
                    value={method}
                    name="method"
                    isDisabled
                    onChange={(selectedOptions) => handleChangeMultiSelect(selectedOptions, 'method')}
                    options={methodOptionList.map(item => ({
                        value: item.id,
                        label: item.name
                    }))}
                    placeholder="Method List"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />  
            </div> :
            <div className='m-5'>
                <Select
                    isMulti
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    className='basic-multi-select'
                    value={method}
                    name="method"
                    onChange={(selectedOptions) => handleChangeMultiSelect(selectedOptions, 'method')}
                    options={methodOptionList.map(item => ({
                        value: item.id,
                        label: item.name
                    }))}
                    placeholder="Method List"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />  
            </div>}
            
            <div className='m-5'>
                <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                >
                    Submit
                </button>
                {filter && 
                 <button
                 onClick={handleClear}
                 className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
             >
                 Clear
             </button>}
            </div>
            {/* {routeName != "summary" && <div>
                <button
                    onClick={handleSummary}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                >
                    {!summary ? 'Summary' : 'Detail'}
                </button>
            </div>} */}
            <div>
                <button
                    onClick={clickExport}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                >
                    Export
                </button>
            </div>
            {/* Report List is opt-in. It used to key on routeName == "master", which matches BOTH
                /reports/master (Raw Report) and /old_reports/master (Old Master Report) -- the two
                screens cannot be told apart by their last path segment. Only the Old Master Report
                asks for it now; the Raw Report drives column visibility from Show Columns instead. */}
            {showReportType && <div className='m-5'>
                <Select
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    value={reportType}
                    name="reportType"
                    onChange={(selectedOption, name) => {
                        // handleSelectChange(selectedOption, name);
                        handleReport(selectedOption);
                    }}
                    options={reportTypeList.map(item => ({
                        value: item.id,
                        label: item.label
                    }))}
                    placeholder="Report List"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />  
            </div>}
        </div>
    );
};

export {DateSessionReportFilter};
